import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button, Table, Tooltip } from '@bedrock/components';
import { Plus, HelpCircle } from '@bedrock/icons-react';
import { useMemoizedFn } from 'ahooks';
import ProTableForm, {
  FormTableRef,
} from '@mpc/common/src/components/ProTableForm';
import {
  v2IpCooperationDemandL1Validate,
  type IpCooperationDemandValidateForm,
} from '#services/api';
import DurationInput, { formatDurationValue } from '../DurationInput';
import type { IPCooperationOnlineDetailItem } from '../utils';

interface OnlineDetailTableProps {
  value?: IPCooperationOnlineDetailItem[];
  onChange?: (value: IPCooperationOnlineDetailItem[]) => void;
  /** 详情只读：隐藏新增/删除，日期单元格纯展示 */
  view?: boolean;
  /** 当前所选IP资源ID（新建态实时冲突预检用） */
  ipId?: number;
}

/** 父表单 watchChildFormValidate 在提交时调用的校验能力 */
export interface OnlineDetailTableRef {
  validateFields: () => Promise<unknown>;
}

/** 中文数字，覆盖到第十次联动即可满足业务场景需要 */
const CN_NUM = ['一', '二', '三', '四', '五', '六', '七', '八', '九', '十'];

const OnlineDetailTable = forwardRef<
  OnlineDetailTableRef,
  OnlineDetailTableProps
>(({ value, onChange, view, ipId }, ref) => {
  const tableRef = useRef<FormTableRef>(null);

  // 新建态实时冲突预检结果：表格数据修改 → validate → 有冲突才展示预警文案
  const [conflict, setConflict] = useState(false);

  // 新建态（非 view）实时预检：上线明细 / 所选IP 变化时防抖调 validate
  // （镜像 NewIPCooperationItem 的 cancelled 竞态防护 + silent 静默；500ms 防抖避免频繁请求）
  useEffect(() => {
    if (view || !ipId) {
      setConflict(false);
      return;
    }
    const details = (value || []).filter(
      (d) => d?.online_begin && d?.online_end
    );
    if (!details.length) {
      setConflict(false);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      const payload: IpCooperationDemandValidateForm = {
        ip_id: ipId,
        online_details: details.map((d) => ({
          online_begin: d.online_begin,
          online_end: d.online_end,
        })),
      };
      v2IpCooperationDemandL1Validate(payload, { silent: true })
        .then((res) => {
          if (!cancelled) setConflict(!!res?.data?.conflict);
        })
        .catch(() => {
          if (!cancelled) setConflict(false);
        });
    }, 500);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [value, ipId, view]);

  // 暴露校验能力：委托内部 ProTableForm.form.validateFields()，使父表单提交时联动校验
  // 各行「预计下线窗口晚于预计上线窗口」（镜像 SettlementAmountTable 范式）
  useImperativeHandle(
    ref,
    () => ({
      validateFields: () => {
        if (!tableRef.current?.form?.validateFields) {
          return Promise.resolve();
        }
        return tableRef.current.form.validateFields().catch((error: any) => {
          const errMessage = error?.errorFields?.[0]?.errors?.[0];
          return Promise.reject(errMessage);
        });
      },
    }),
    []
  );

  const formColumns = useMemo(() => {
    return ({
      remove,
    }: {
      add: any;
      remove: (i: number | number[]) => void;
    }) => {
      return [
        {
          title: '联动阶段',
          dataIndex: 'stage',
          width: 100,
          render: (_t: any, _r: IPCooperationOnlineDetailItem, index: number) =>
            `第${CN_NUM[index] || index + 1}次联动`,
        },
        {
          title: (
            <span>
              预计上线窗口
              <Tooltip
                title="时间范围较长，请尽量控制在3个月内，便于评估排期"
                placement="top"
              >
                <HelpCircle className="m-l-2" size={14} />
              </Tooltip>
            </span>
          ),
          dataIndex: '$online_at' as any,
          width: 260,
          required: true,
          // view 态由 SFormElement 走 showComponent 渲染只读文本（镜像 ProductInfoTable 联动所属区域列范式）
          formItem: (_t: any, record: IPCooperationOnlineDetailItem) => ({
            view,
            componentClass: 'datePicker',
            formItemKeyMap: {
              '[0]': 'online_begin',
              '[1]': 'online_end',
            },
            elementProps: {
              range: true,
              picker: 'month',
              format: 'YYYY-MM',
              showComponent: () =>
                [record.online_begin, record.online_end]
                  .filter(Boolean)
                  .join(' ~ ') || '--',
            },
          }),
        },
        {
          title: '预计下线窗口',
          dataIndex: '$offline_at' as any,
          width: 260,
          // view 态由 showComponent 渲染只读文本（镜像「预计上线窗口」列范式）
          formItem: (_text: any, record: IPCooperationOnlineDetailItem) => ({
            view,
            componentClass: 'datePicker',
            formItemKeyMap: {
              '[0]': 'offline_begin',
              '[1]': 'offline_end',
            },
            elementProps: {
              range: true,
              picker: 'month',
              format: 'YYYY-MM',
              showComponent: () =>
                [record.offline_begin, record.offline_end]
                  .filter(Boolean)
                  .join(' ~ ') || '--',
            },
            rules: [
              {
                validator: (_rule: any, val: any) => {
                  const [offlineBegin, offlineEnd] = val || [];
                  // ① 联动下线最早时间 > 联动上线最早时间
                  if (
                    offlineBegin &&
                    record?.online_begin &&
                    offlineBegin <= record.online_begin
                  ) {
                    return Promise.reject(
                      '联动下线的最早下线时间必须晚于上线的最早上线时间'
                    );
                  }
                  // ② 联动下线最晚时间 > 联动上线最晚时间
                  if (
                    offlineEnd &&
                    record?.online_end &&
                    offlineEnd <= record.online_end
                  ) {
                    return Promise.reject(
                      '联动下线的最后下线时间必须晚于上线的最晚上线时间'
                    );
                  }
                  return Promise.resolve();
                },
              },
            ],
          }),
        },
        {
          title: '预计联动时长',
          dataIndex: '$duration' as any,
          width: 220,
          // 预计联动时长必填：数值（duration）与单位（unit）均须填写（镜像 SecondaryOnlineDetailTable $duration 范式）
          required: !view,
          // 只读态纯文本展示；编辑态用共享 DurationInput 组合控件 + 对象形态 formItemKeyMap 分发到 duration / unit
          ...(view
            ? {
                render: (_t: any, record: IPCooperationOnlineDetailItem) =>
                  formatDurationValue({
                    duration: record.duration,
                    unit: record.unit,
                  }),
              }
            : {
                formItem: {
                  render: () => <DurationInput />,
                  formItemKeyMap: {
                    duration: 'duration',
                    unit: 'unit',
                  },
                  rules: [
                    {
                      validator: (_rule: any, value: any) => {
                        if (
                          value?.duration == null ||
                          value?.duration === '' ||
                          value?.unit == null
                        ) {
                          return Promise.reject(
                            new Error('请填写预计联动时长')
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ],
                },
              }),
        },
        // 详情只读：不显示「操作」列
        ...(view
          ? []
          : [
              {
                title: '操作',
                dataIndex: 'operate',
                width: 60,
                fixed: 'right' as const,
                render: (
                  _t: any,
                  _r: IPCooperationOnlineDetailItem,
                  index: number
                ) => (
                  <Button
                    type="link"
                    size="small"
                    danger
                    disabled={(value?.length ?? 0) <= 1}
                    onClick={() => remove(index)}
                  >
                    删除
                  </Button>
                ),
              },
            ]),
      ];
    };
  }, [view, value]);

  const handleAdd = useMemoizedFn(() => {
    tableRef.current?.add({});
  });


  return (
    <div>
      <ProTableForm<IPCooperationOnlineDetailItem>
        ref={tableRef}
        value={value}
        onChange={onChange}
        sortable={false}
        size="small"
        scroll={{ x: 'max-content' }}
        formColumns={formColumns as any}
        summary={
          view
            ? undefined
            : () => (
                <Table.Summary>
                  <Table.Summary.Row style={{ background: '#fff' }}>
                    <Table.Summary.Cell index={0} colSpan={5}>
                      <Button
                        type="checked"
                        icon={<Plus />}
                        onClick={handleAdd}
                        className="mb-2"
                      >
                        新增次数
                      </Button>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              )
        }
      />
      {conflict && (
        <div
          className="fs-12 m-t-4"
          style={{ color: 'var(--status-warning-1)' }}
        >
          存在上线日期范围内进行中的IP合作需求，请先咨询IP管理组（陈妍、高沁、聂宏阳）
        </div>
      )}
    </div>
  );
});

OnlineDetailTable.displayName = 'OnlineDetailTable';

export default OnlineDetailTable;
